import momentTZ from "moment-timezone";

import { PAYMENT_METHOD_KINDS } from "api/payout-api";

export const WIRE_TYPES = {
  deposit: "deposit",
  payout: "payout",
};

export const PAYOUT_SOURCES = {
  manual: "manual",
  automatic: "automatic",
  support: "support",
};

const NY_TZ = "America/New_York";
const icon = (name) => `${process.env.PUBLIC_URL}/icons/${name}.svg`;

/**
 * Backend wire `type` is "initial" | "default" for deposits and "payout"
 * for pay-outs.
 */
export const getWireType = (wire) =>
  (wire?.type || "").toLowerCase() === WIRE_TYPES.payout
    ? WIRE_TYPES.payout
    : WIRE_TYPES.deposit;

export const isPayoutWire = (wire) => getWireType(wire) === WIRE_TYPES.payout;

export const isAutomaticPayout = (wire) => !!wire?.payoutData?.isAuto;

/**
 * manual | automatic | support.
 * `isAuto` is defined by the backend; the "created by Support" marker is
 * assumed (payoutData.source / createdBy) until the backend defines it.
 */
export const getPayoutSource = (wire) => {
  if (!isPayoutWire(wire)) return null;
  if (isAutomaticPayout(wire)) return PAYOUT_SOURCES.automatic;
  const data = wire?.payoutData || {};
  const marker = String(data.source || data.createdBy || "").toLowerCase();
  if (marker === "support" || marker === "staff" || marker === "admin" || data.isStaff) {
    return PAYOUT_SOURCES.support;
  }
  return PAYOUT_SOURCES.manual;
};

const SOURCE_LABELS = {
  [PAYOUT_SOURCES.manual]: "(manual)",
  [PAYOUT_SOURCES.automatic]: "(automatic)",
  [PAYOUT_SOURCES.support]: "(Support)",
};

export const getWireTypeDisplay = (wire) => {
  const type = getWireType(wire);
  const isPayout = type === WIRE_TYPES.payout;
  const source = getPayoutSource(wire);
  const isAuto = source === PAYOUT_SOURCES.automatic;
  return {
    type,
    isPayout,
    isAuto,
    source,
    title: isPayout ? "Pay-out" : "Deposit",
    subtitle: isPayout ? SOURCE_LABELS[source] : "",
    label: isPayout ? `Pay-out ${SOURCE_LABELS[source]}` : "Deposit",
    iconPath: icon(isPayout ? "payout" : "wire-confirmation"),
    iconClass: isPayout ? "type-icon-payout" : "type-icon-deposit",
  };
};

/**
 * Maps the backend wire status to the portal wording.
 * Deposits: pending/claimed -> IN PROGRESS, received -> CONFIRMED, cancelled -> CANCELLED
 * Pay-outs: pending -> PENDING, claimed -> IN PROGRESS, received -> DONE,
 *           declined -> DECLINED, cancelled -> CANCELLED
 */
export const getWireStatusDisplay = (wire) => {
  const isPayout = isPayoutWire(wire);
  const raw = (wire?.status || wire?.state || "").toUpperCase();

  const inProgress = { text: "IN PROGRESS", iconPath: icon("inprogress"), className: "status-in-progress" };
  const pending = { text: "PENDING", iconPath: icon("inprogress"), className: "status-pending" };

  if (raw === "PENDING" || raw === "NEW" || raw === "REQUESTED") {
    return isPayout ? pending : inProgress;
  }
  if (raw.includes("PROGRESS") || raw === "PROCESSING" || raw === "CLAIMED") {
    return inProgress;
  }
  if (raw.includes("CONFIRM") || raw === "COMPLETED" || raw === "RECEIVED" || raw === "SENT" || raw === "DONE") {
    return { text: isPayout ? "DONE" : "CONFIRMED", iconPath: icon("done"), className: "status-confirmed" };
  }
  if (raw.includes("DECLIN") || raw === "REJECTED" || raw === "FAILED") {
    return { text: "DECLINED", iconPath: icon("decline"), className: "status-declined" };
  }
  if (raw === "CANCELLED" || raw === "CANCELED") {
    return { text: "CANCELLED", iconPath: icon("decline"), className: "status-cancelled" };
  }
  return isPayout ? pending : inProgress;
};

export const OPEN_PAYOUT_STATUSES = ["pending", "claimed", "new", "requested", "inprogress", "in_progress"];

export const isOpenPayout = (wire) =>
  isPayoutWire(wire) && OPEN_PAYOUT_STATUSES.includes(String(wire?.status || "").toLowerCase());

export const isPendingPayout = (wire) =>
  isPayoutWire(wire) && ["pending", "new", "requested"].includes(String(wire?.status || "").toLowerCase());

/** Any pay-out that has not been resolved yet (blocks bank-detail changes). */
export const getOpenPayouts = (wiresList) => (Array.isArray(wiresList) ? wiresList : []).filter(isOpenPayout);

/** The active manual request, if any (only one may be active at a time). */
export const getActiveManualPayout = (wiresList) =>
  getOpenPayouts(wiresList).find((w) => getPayoutSource(w) === PAYOUT_SOURCES.manual) || null;

export const getWireCreatedDate = (wire) =>
  wire?.requestedOn || wire?.createdAt || wire?.created || wire?.date || null;

export const getWireUpdatedDate = (wire) =>
  wire?.received?.date || wire?.claim?.date || wire?.updatedAt || wire?.updated || null;

/** "19 Oct 2026 at 09:35" (New York time) or "June 2026" for monthly records. */
export const formatWireDate = (value, { monthOnly = false } = {}) => {
  if (!value) return "";
  const m = momentTZ(value).tz(NY_TZ);
  if (!m.isValid()) return String(value);
  return monthOnly ? m.format("MMMM YYYY") : m.format("D MMM YYYY [at] HH:mm");
};

export const formatWireAmount = (amount) => {
  if (typeof amount === "number") {
    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  }
  return amount || "$0.00";
};

export const getWireAmount = (wire) =>
  wire?.amount?.claimed ?? wire?.amount?.pending ?? wire?.amount?.received ?? 0;

export const parseAmountInput = (value) =>
  parseFloat(String(value ?? "").replace(/[^0-9.]/g, ""));

/**
 * Short description for a saved payment method
 * (e.g. "Privat Bank" or "not set").
 */
export const getPaymentMethodSummary = (kind, method) => {
  if (!method) return "not set";
  if (kind === PAYMENT_METHOD_KINDS.bankAccount) {
    return method.bankName || "set";
  }
  return "set";
};

/**
 * Frozen bank-detail snapshot attached to a pay-out at creation.
 * Needs the backend to include `payoutData` in the wire list (not yet).
 */
export const getPayoutSnapshot = (wire) => wire?.payoutData?.settingsSnapshot?.data || null;

/** "···· 6789" for an account number / IBAN. */
export const maskAccountNumber = (value) => {
  const code = String(value || "").replace(/\s/g, "");
  if (!code) return "";
  return `···· ${code.slice(-4)}`;
};

/** "Routing number 021000021" or "BIC / SWIFT DEUTDEFF". */
export const formatBankCode = (code) => {
  const value = String(code || "").trim();
  if (!value) return "";
  return /^\d+$/.test(value) ? `Routing number ${value}` : `BIC / SWIFT ${value}`;
};

/**
 * Reason an automatic pay-out was declined (e.g. funds below Minimum PB).
 * Field name assumed until the backend defines it.
 */
export const getPayoutDeclineReason = (wire) =>
  wire?.payoutData?.declineReason || wire?.declineReason || "";

// --- Widget state helpers (top-of-page cards) ------------------------------

export const isDepositWire = (wire) => getWireType(wire) === WIRE_TYPES.deposit;

/** A wire the broker has not resolved yet (pending / claimed / processing). */
export const isOpenWire = (wire) =>
  OPEN_PAYOUT_STATUSES.includes(String(wire?.status || "").toLowerCase());

/** pending | in_progress — how an open pay-out is shown in a widget badge. */
export const getPayoutProgress = (wire) => {
  const s = String(wire?.status || "").toLowerCase();
  return ["claimed", "processing", "inprogress", "in_progress"].includes(s)
    ? "in_progress"
    : "pending";
};

/** Deposit confirmations the trader has sent that Support has not matched yet. */
export const getPendingDeposits = (wiresList) =>
  (Array.isArray(wiresList) ? wiresList : []).filter(
    (w) => isDepositWire(w) && isOpenWire(w)
  );

/** The current month's automatic pay-out request while it is still open. */
export const getCurrentAutoPayout = (wiresList) =>
  getOpenPayouts(wiresList).find(
    (w) => getPayoutSource(w) === PAYOUT_SOURCES.automatic
  ) || null;

/** The most recent automatic pay-out that was declined (newest-first list). */
export const getLastDeclinedAutoPayout = (wiresList) =>
  (Array.isArray(wiresList) ? wiresList : []).find(
    (w) =>
      getPayoutSource(w) === PAYOUT_SOURCES.automatic &&
      ["declined", "rejected", "failed"].includes(
        String(w?.status || "").toLowerCase()
      )
  ) || null;

/** Any open pay-out (manual or automatic) — bank-detail edits are locked then. */
export const hasActivePayout = (wiresList) => getOpenPayouts(wiresList).length > 0;
